import React from 'react';
import { Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import { getFunnelAnchor } from '../userFunnel.constants';
import styles from './styles/user-funnel-anchor.module.scss';

/**
 * The step the member is standing on, rendered as the page's visual anchor.
 *
 * This exists to replace guessing at a member's state, which only works if the
 * answer is the first and largest thing an agent sees — so the stage name is
 * set deliberately larger than the member's own name in `UserHeader`, and this
 * sits directly beneath it, above the KPIs and every card.
 *
 * Renders nothing while the funnel is loading or failed: the timeline card
 * below owns those states and says so properly. A silent gap here is better
 * than an anchor that announces a stage it does not actually know.
 *
 * @param {Object} props
 * @param {Object | null} [props.funnel] Funnel payload.
 * @param {boolean} [props.loading]
 * @param {string | null} [props.error]
 */
function UserFunnelAnchor({ funnel, loading, error }) {
    const anchor = loading || error ? null : getFunnelAnchor(funnel);

    if (!anchor) return null;

    // "Current step", not "Currently stuck on": `current_stage` is just the
    // first stage the member has not completed, so it names where they are —
    // a member who signed in a minute ago is standing on a step, not stuck on
    // it. Same wording the card's own pill uses.
    //
    // A finished journey has no next step, and `unknown` — the scan having
    // found nothing — has no known one, so neither may sit under an eyebrow
    // promising to name one.
    const namesAStep = !anchor.complete && !anchor.unknown;
    const eyebrow = namesAStep ? 'Current step' : 'Onboarding journey';

    return (
        <section className={styles.anchor} aria-label={namesAStep ? 'Current onboarding step' : 'Onboarding journey'}>
            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.eyebrow}>
                {eyebrow}
            </Text>

            <div className={styles.headline}>
                <Text
                    as={TextAs.H2}
                    size={TextSizes.LARGE}
                    className={`${styles.stage} ${anchor.complete ? styles.stageComplete : ''}`}
                >
                    {anchor.title}
                </Text>

                {anchor.hasError ? (
                    <UserStatePill
                        tone={anchor.errorResolved ? 'success' : 'danger'}
                        label={anchor.errorResolved ? 'Error resolved' : 'Unresolved error'}
                    />
                ) : null}
            </div>

            {anchor.description ? (
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.description}>
                    {anchor.description}
                </Text>
            ) : null}
        </section>
    );
}

export default UserFunnelAnchor;
